import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Autocomplete,
  TableSortLabel,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { vetsAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { US_STATES } from '../constants/usStates';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import { toArray } from '../utils/apiData';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

// Phone number formatting function
const formatPhoneNumber = (phone) => {
  if (!phone) return '-';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Check if it's a valid US phone number (10 digits)
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Check if it's a valid US phone number with country code (11 digits starting with 1)
  if (cleaned.length === 11 && cleaned[0] === '1') {
    const withoutCountryCode = cleaned.slice(1);
    return `(${withoutCountryCode.slice(0, 3)}) ${withoutCountryCode.slice(3, 6)}-${withoutCountryCode.slice(6)}`;
  }
  
  // Return original if it doesn't match expected formats
  return phone;
};

const VetsPage = () => {
  const notify = useNotify();
  const [vets, setVets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingVet, setEditingVet] = useState(null);
  const [sortBy, setSortBy] = useState('clinic_name');
  const [sortOrder, setSortOrder] = useState('asc');

  const [formData, setFormData] = useState({
    clinic_name: '',
    vet_name: '',
    phone: '',
    email: '',
    emergency_phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    specialties: '',
    notes: '',
  });

  const loadVets = useCallback(async () => {
    setLoadError('');
    try {
      const params = {
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      const response = await vetsAPI.getAll(params);
      setVets(toArray(response.data, 'vets'));
    } catch (error) {
      console.error('Error loading vets:', error);
      // The previous list is left in place on purpose: a failed refresh should say so
      // rather than blank the page out into something indistinguishable from "no vets".
      setLoadError(error?.response?.data?.error || 'Unable to load veterinarians. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    loadVets();
  }, [loadVets]);

  useRefreshOnFocus(loadVets);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleOpenDialog = (vet = null) => {
    if (vet) {
      setEditingVet(vet);
      setFormData(vet);
    } else {
      setEditingVet(null);
      setFormData({
        clinic_name: '',
        vet_name: '',
        phone: '',
        email: '',
        emergency_phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        specialties: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingVet(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (editingVet) {
        await vetsAPI.update(editingVet.id, formData);
      } else {
        await vetsAPI.create(formData);
      }
      handleCloseDialog();
      loadVets();
    } catch (error) {
      console.error('Error saving vet:', error);
      notify.error(errorMessage(error, 'Error saving vet'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (vet) => setDeleteTarget(vet);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await vetsAPI.delete(deleteTarget.id);
      setDeleteTarget(null);
      loadVets();
    } catch (error) {
      console.error('Error deleting vet:', error);
      notify.error(errorMessage(error, 'Error deleting vet'));
    }
  };


  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'flex-start', sm: 'space-between' },
          alignItems: 'center',
          gap: { xs: 1.5, sm: 0 },
          mb: 3,
        }}
      >
        <Typography variant="h4">Veterinarians</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            minWidth: { xs: 0, sm: 'auto' },
            width: { xs: 32, sm: 'auto' },
            height: { xs: 32, sm: 'auto' },
            px: { xs: 0, sm: 2 },
            '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Add Veterinarian
          </Box>
        </Button>
      </Box>
      {loadError ? (
        <Alert severity="error" sx={{ mb: 0.5 }}>
          {loadError}
        </Alert>
      ) : null}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'clinic_name'}
                  direction={sortBy === 'clinic_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('clinic_name')}
                >
                  Clinic Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'vet_name'}
                  direction={sortBy === 'vet_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('vet_name')}
                >
                  Vet Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'phone'}
                  direction={sortBy === 'phone' ? sortOrder : 'asc'}
                  onClick={() => handleSort('phone')}
                >
                  Phone
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'email'}
                  direction={sortBy === 'email' ? sortOrder : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'city'}
                  direction={sortBy === 'city' ? sortOrder : 'asc'}
                  onClick={() => handleSort('city')}
                >
                  City, State
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={6} />}
            {vets.map((vet) => (
              <TableRow key={vet.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  <RowActionsMenu
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(vet) },
                      { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(vet), color: 'error' },
                    ]}
                  />
                </TableCell>
                <TableCell>{vet.clinic_name}</TableCell>
                <TableCell>{vet.vet_name || '-'}</TableCell>
                <TableCell>{formatPhoneNumber(vet.phone)}</TableCell>
                <TableCell>{vet.email || '-'}</TableCell>
                <TableCell>
                  {vet.city && vet.state ? `${vet.city}, ${vet.state}` : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVet ? 'Edit Veterinarian' : 'Add New Veterinarian'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="clinic_name"
                  label="Clinic Name"
                  value={formData.clinic_name}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="vet_name"
                  label="Veterinarian Name"
                  value={formData.vet_name}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="phone"
                  label="Phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="emergency_phone"
                  label="Emergency Phone"
                  value={formData.emergency_phone}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="address"
                  label="Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="city"
                  label="City"
                  value={formData.city}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={US_STATES}
                  getOptionLabel={(option) => option.name}
                  filterOptions={createPrefixFilter((option) => option.name)}
                  value={US_STATES.find(state => state.code === formData.state) || null}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({ ...prev, state: newValue ? newValue.code : '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      {...params}
                      label="State"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        {option.code} - {option.name}
                      </Box>
                    );
                  }}
                  isOptionEqualToValue={(option, value) => option.code === value?.code}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="zip_code"
                  label="Zip Code"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="specialties"
                  label="Specialties"
                  value={formData.specialties}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="e.g., Equine Surgery, Internal Medicine, Emergency Care"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="notes"
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingVet ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deactivate Veterinarian"
        message={`Deactivate veterinarian "${deleteTarget?.clinic_name}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLabel="Deactivate"
        confirmColor="warning"
      />
    </Box>
  );
};

export default VetsPage;
